import Slider from 'react-slick';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
// material
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Card, Link, Stack, Avatar, Rating, Typography } from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
import { CarouselControlsArrowsBasic2 } from '../../../components/carousel/controls';
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
import useResponsive from '../../../hooks/useResponsive';
import useLazyMockWeb from '../../../hooks/useLazyMockWeb';
import { resolveAggregate } from '../../../utils/reviewAggregate';
import { hasConfirmedRating } from '../../../utils/reviewRating';
import { SectionHeader } from '../common';
import { GoogleReviewsUrl } from '../../../config';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getGoogleReviews } from '../../../redux/slices/course';
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

ReviewCard.propTypes = {
  item: PropTypes.object.isRequired,
};

// A single review styled like a Google review card: reviewer header, Google
// mark, star rating and the review body.
function ReviewCard({ item }) {
  const { user, rating, body, addedAt } = item;

  // Only a rating Google actually reported may be shown. The stored reviews were
  // transcribed from a listing crawl that never captured star counts, so those
  // rows arrive with no rating and must render without the star row rather than
  // with a fabricated five. Reviews read live from Place Details do carry one and
  // are shown as normal.
  const showRating = hasConfirmedRating(rating);
  const starValue = showRating ? Number(rating) : null;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2.25, sm: 2.5 },
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
        '@media (hover: hover)': {
          '&:hover': {
            transform: 'translateY(-6px)',
            borderColor: 'transparent',
            boxShadow: (theme) => `0 18px 36px ${alpha(theme.palette.grey[900], 0.12)}`,
          },
        },
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <Avatar alt={user.name} src={user.avatar} sx={{ width: 44, height: 44 }} />

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="subtitle2" noWrap sx={{ color: 'text.primary' }}>
            {user.name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {fToNow(addedAt)}
          </Typography>
        </Box>

        <Iconify icon="logos:google-icon" width={22} sx={{ flexShrink: 0 }} />
      </Stack>

      {showRating && (
        <Rating value={starValue} precision={0.5} readOnly size="small" sx={{ mt: 1.75 }} />
      )}

      <Typography
        variant="body2"
        sx={{
          // Without a star row the body needs the top margin the rating used to
          // give it, or the text sits flush against the reviewer name.
          mt: showRating ? 1.5 : 1.25,
          color: 'text.secondary',
          lineHeight: 1.85,
          flexGrow: 1,
          display: '-webkit-box',
          // A phone shows one full-width card, so it can afford more text per
          // card than the narrower multi-column layouts.
          WebkitLineClamp: { xs: 6, sm: 5 },
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {body}
      </Typography>
    </Card>
  );
}

// ----------------------------------------------------------------------

ReviewsCarousel.propTypes = {
  reviews: PropTypes.array.isRequired,
};

function ReviewsCarousel({ reviews }) {
  const theme = useTheme();
  const carouselRef = useRef(null);

  // One slide on a phone, two from md, three from lg. These mirror the
  // react-slick breakpoints below so the arrows, the looping and the number of
  // visible cards can never disagree with what the slider really renders.
  const isUpLg = useResponsive('up', 'lg');
  const isUpMd = useResponsive('up', 'md');

  const slidesToShow = isUpLg ? 3 : isUpMd ? 2 : 1;
  // The arrows are laid over the outer cards, so they only appear where there is
  // room for them and where there is something to scroll to. Phones swipe.
  const canScroll = reviews.length > slidesToShow;
  const showArrows = isUpMd && canScroll;

  const settings = {
    speed: 600,
    dots: false,
    arrows: false,
    autoplay: canScroll,
    autoplaySpeed: 4500,
    pauseOnHover: true,
    swipeToSlide: true,
    infinite: canScroll,
    slidesToShow,
    slidesToScroll: 1,
    rtl: Boolean(theme.direction === 'rtl'),
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 3 } },
      { breakpoint: 900, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } },
    ],
  };

  const handlePrevious = () => {
    carouselRef.current?.slickPrev();
  };

  const handleNext = () => {
    carouselRef.current?.slickNext();
  };

  return (
    <Card
      sx={{
        position: 'relative',
        p: { xs: 1.5, sm: 2 },
        borderRadius: 3,
        // Keep every slide the same height so the cards line up.
        '& .slick-track': { display: 'flex', alignItems: 'stretch' },
        '& .slick-slide': { height: 'auto' },
        '& .slick-slide > div': { height: '100%' },
      }}
    >
      <Slider ref={carouselRef} {...settings}>
        {reviews.map((item) => (
          <Box key={item.id} sx={{ height: '100%', p: { xs: 0.5, sm: 0.75 } }}>
            <ReviewCard item={item} />
          </Box>
        ))}
      </Slider>

      {showArrows && <CarouselControlsArrowsBasic2 onNext={handleNext} onPrevious={handlePrevious} />}
    </Card>
  );
}

// ----------------------------------------------------------------------

GoogleTestimonials.propTypes = {
  skipFetch: PropTypes.bool,
};

export default function GoogleTestimonials({ skipFetch = false }) {
  const dispatch = useDispatch();
  const { googleReviews, googleReviewMeta, isLoading } = useSelector((state) => state.course);

  // Last resort, used only when both the backend endpoint and the live Google
  // Places lookup are unavailable.
  const [fallbackReviews, fallbackReady] = useLazyMockWeb(
    'googleReviews',
    !isLoading && !(Array.isArray(googleReviews) && googleReviews.length)
  );

  useEffect(() => {
    if (!skipFetch) {
      dispatch(getGoogleReviews());
    }
  }, [dispatch, skipFetch]);

  const reviews = Array.isArray(googleReviews) && googleReviews.length ? googleReviews : fallbackReviews;

  if (!reviews.length && (isLoading || !fallbackReady)) {
    return (
      <Box component="section" sx={{ mt: { xs: 5, md: 8 } }}>
        <Typography variant="subtitle1" sx={{ color: '#000', display: 'block', textAlign: 'center' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // The aggregate figures, or nothing at all when there is nothing sourced to show.
  //
  // `resolveAggregate` owns the whole decision and returns null for anything that
  // was not reported by Google. Both figures come from Google — either from the
  // live Places lookup or from the backend bundle's stored `review_meta`, which
  // share one shape. Neither is ever computed from the cards below: Place Details
  // returns at most five reviews, so averaging them would describe the sample
  // rather than the business. The derivation this replaced is the defect, and it
  // is not reintroduced here.
  const { rating, totalReviews, showRating, showTotal, showSummary } = resolveAggregate(
    googleReviewMeta
  );

  return (
    <Box component="section" sx={{ mt: { xs: 5, md: 8 } }}>
      <SectionHeader
        eyebrow="Google reviews"
        title="What Our Community Says"
        description="Hear from students who have taken the path to success with ASAP."
      />

      {/* No stored figure means no aggregate line at all. The cards below still
          render, and the "View more on Google" link still carries the place id,
          which is the one value Google's terms exempt from the caching
          restriction. */}
      {showSummary && (
        <MotionInView variants={varFadeInUp}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.5, sm: 1.5 }}
            sx={{ mb: { xs: 3, md: 4 }, alignItems: 'center', justifyContent: 'center' }}
          >
            <Iconify icon="logos:google-icon" width={26} />
            {showRating && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {/* Decorative aggregate figure, not a heading. */}
                <Typography
                  component="span"
                  variant="h5"
                  sx={{ color: 'text.primary', fontWeight: 800 }}
                >
                  {rating.toFixed(1)}
                </Typography>
                <Rating value={rating} precision={0.1} readOnly size="small" />
              </Stack>
            )}
            {showTotal && (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Based on {totalReviews} Google reviews
              </Typography>
            )}
          </Stack>
        </MotionInView>
      )}

      <MotionInView variants={varFadeInUp} transition={{ delay: 0.1 }}>
        <ReviewsCarousel reviews={reviews} />
      </MotionInView>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2.5, md: 3 } }}>
        <Link
          href={GoogleReviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="subtitle2"
          underline="hover"
          sx={{
            color: '#DF3E30',
            display: 'inline-flex',
            alignItems: 'center',
            transition: 'transform 0.3s ease',
            '@media (hover: hover)': {
              '&:hover': { transform: 'translateX(4px)' },
            },
          }}
        >
          <Iconify icon="mdi:google-reviews" width={20} sx={{ mr: 0.5 }} />
          View more on Google
        </Link>
      </Box>
    </Box>
  );
}
